import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  RendererFactory2,
  signal,
  ViewChild,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { filter } from 'rxjs/operators';
import { selectCurrentRoute } from '~/shared-core/router';
import { SeoService } from '~/shared-core/utils';
import { ToolbarComponent } from '~/shared-ui';
import { SharedModule } from '../shared/shared.module';
import { AuthStore, LocalStorageJwtService } from '../stores/auth';
import { CloudTagListStore } from '../stores/cloud-tag/cloud-tag-list.store';
import { MyCollectionListStore } from '../stores/my-collection/my-collection-list.store';
import { OrderDetailStore } from '../stores/order/order-detail.store';
import { TopbarService } from '../stores/services/topbar.service';
import { LanguagesListStore } from '../stores/settings/languages-list.store';
import { ThemeService } from '../stores/themes/theme.service';
import { AppFooterB2BComponent } from './footer B2B/app-footer.component';
import { AppFooterComponent } from './footer/app-footer.component';
import { AppSidebarComponent } from './sidebar/app-sidebar.component';
import { AppTopbarB2BComponent } from './topbar B2B/app-topbar.component';
import { AppTopbarComponent } from './topbar/app-topbar.component';
import { AppTopbarNewComponent } from './topbar B2C New/app-topbar.component';

import { environment } from '../../environments/environment';

interface AiAssistantMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface AiAssistantMessageSegment {
  type: 'text' | 'bold' | 'product' | 'card' | 'detail' | 'external';
  text: string;
  url: string;
  productName: string;
  price?: string;
  imageUrl?: string;
  reason?: string;
  review?: string;
  optionNames?: string[];
}

@Component({
  selector: 'app-layout',
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    AppTopbarComponent,
    AppTopbarNewComponent,
    AppTopbarB2BComponent,
    AppFooterComponent,
    DialogModule,
    ButtonModule,
    ProgressSpinnerModule,
    AppFooterB2BComponent,
    AppSidebarComponent,
    ToolbarComponent,
    SharedModule,
  ],
})
export class AppLayoutComponent {
  @ViewChild('aiAssistantContent')
  private aiAssistantContent?: ElementRef<HTMLDivElement>;

  private readonly store = inject(Store);
  private $cloudTagListStore = inject(CloudTagListStore);
  private $orderDetailStore = inject(OrderDetailStore);
  private $myCollectionListStore = inject(MyCollectionListStore);
  private seoService = inject(SeoService);
  private router = inject(Router);
  private localStorageJwtService = inject(LocalStorageJwtService);
  private languagesListStore = inject(LanguagesListStore);
  private topbarService = inject(TopbarService);

  private readonly authStore = inject(AuthStore);
  private themeService = inject(ThemeService);

  currentRoute$ = this.store.select(selectCurrentRoute);
  $currentUser = this.authStore.user;
  $languageList: any = this.languagesListStore.languageList.records;

  company_name = this.$currentUser()?.company?.company_name as any;
  currentTheme: string = ''; // Default theme
  themeURL: string = ''; // Default themeURL
  isOnPayment: any = false;
  readonly $isAiAssistantOpen = signal(false);
  readonly $isAiAssistantExpanded = signal(false);
  readonly $aiAssistantMessages = signal<AiAssistantMessage[]>([]);
  readonly $isAiAssistantLoading = signal(false);
  readonly $showHumanAgent = signal(false);
  readonly $showContactConfirmation = signal(false);
  readonly $showReportProblemOptions = signal(false);
  readonly $isContactRequestSending = signal(false);
  aiAssistantInput = '';
  private aiAssistantHistoryUserId = '';

  off_splash: any = 0;
  splash_status: any = 0;
  splash_image: any = '';
  splash_url: any = '';
  splash_file: any = '';

  $loggedIn = this.authStore.loggedIn;
  $isAuthLoaded = this.authStore.isLoaded;
  readonly $showAiAssistantSuggestions = computed(
    () =>
      this.$loggedIn() &&
      !this.$currentUser()?.default_user &&
      !this.$aiAssistantMessages().some((message) => message.role === 'user'),
  );
  readonly $showAiAssistantGuestSuggestions = computed(
    () =>
      (!this.$loggedIn() || !!this.$currentUser()?.default_user) &&
      !this.$aiAssistantMessages().some((message) => message.role === 'user'),
  );
  readonly $showShoppingAdviceSuggestion = computed(
    () =>
      !this.$aiAssistantMessages().some((message) => message.role === 'user'),
  );
  $isLoading = computed(() => {
    return this.$cloudTagListStore.callState() !== 'loaded';
  });
  constructor(rendererFactory: RendererFactory2) {
    this.themeService.renderer = rendererFactory.createRenderer(null, null);
    effect(() => {
      const historyUserId = this.getAiAssistantHistoryUserId();
      if (historyUserId) this.loadAiAssistantHistory(historyUserId);

      if (this.$currentUser()?._id) {
        this.off_splash =
          this.localStorageJwtService.getItemSTR('off_splash') ?? 0;

        this.splash_status = this.$currentUser()?.company?.splash_status;
        this.splash_image = this.$currentUser()?.company?.splash_image;
        this.splash_url = this.$currentUser()?.company?.splash_url;
        this.splash_file = this.$currentUser()?.company?.splash_file;

        if (this.off_splash) {
          this.splash_status = 0;
        }
        if (this.seoService.getTitle() === 'worktraq-app') {
          const company = this.$currentUser()?.company;
          const website_title = company?.website_title || '';
          this.seoService.website_title = this.seoService.getFormattedTitle(
            website_title,
            company,
          );
          this.seoService.setTitle(website_title);

          const website_description = this.$currentUser()?.company
            ?.website_description as any;
          this.seoService.setOneSeoTag('description', website_description);
        }
        if (this.$currentUser() && !this.$currentUser()?.default_user) {
          this.$myCollectionListStore.getList([this.$currentUser()] as any);
        }
        const favicon_file = this.$currentUser()?.company?.favicon_file as any;
        if (favicon_file)
          this.seoService.changeIcon(favicon_file, environment.s3_url);
        else
          this.seoService.changeIcon(
            this.$currentUser()?.company?.logo_file as any,
          );
        const currentPath = window.location.pathname;

        if (currentPath === '/') {
          this.seoService.resetImageMetaToFavIcon(
            favicon_file,
            environment.s3_url,
          );
        }

        const company = this.$currentUser()?.company;
        const google_tag_id = company?.google_tag_id as any;
        const company_name = company?.company_name || '';
        const themeCss = company?.theme_css ?? null;
        this.currentTheme = company_name
          ? company_name.replace(/\s+/g, '')
          : '';
        this.themeURL = themeCss
          ? themeCss.startsWith('assets/')
            ? themeCss
            : `assets/themes/B2B/${themeCss.endsWith('.css') ? themeCss : themeCss + '.css'}`
          : '';
        if (themeCss) {
          this.themeService.setTheme(this.currentTheme, this.themeURL);
        } else {
          this.themeService.removeExistingTheme();
        }

        const website_title = company?.website_title || '';
        this.seoService.website_title = this.seoService.getFormattedTitle(
          website_title,
          company,
        );
        this.seoService.setTitle(website_title || company_name);

        this.seoService.loadGtag(google_tag_id);

        let params: any = [
          this.localStorageJwtService.getItemSTR('company_id'),
          this.localStorageJwtService.getItemSTR('use_company_data') ?? 0,
          this.$currentUser(),
        ];
        this.$cloudTagListStore.getMenuData(params);
      }
    });
  }

  isLogging = false;

  ngOnInit() {
    this.currentRoute$.pipe().subscribe((route) => {
      this.isLogging = !!route?.data?.isLogging;
    });
    this.router.events.subscribe((event) => {
      if (
        event instanceof NavigationStart &&
        event.navigationTrigger === 'popstate'
      ) {
        this.isOnPayment = false;
      }

      if (event instanceof NavigationStart) {
        const url: any = event.url;

        if (url.indexOf('order/detail') !== -1) {
          this.isOnPayment = false;
        }
      }
    });

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        const currentPath = (event as NavigationEnd).urlAfterRedirects;
        if (currentPath === '/') {
          if (this.$currentUser()) {
            const favicon_file = this.$currentUser()?.company
              ?.favicon_file as any;
            this.seoService.resetImageMetaToFavIcon(
              favicon_file,
              environment.s3_url,
            );

            const website_title = this.$currentUser()?.company
              ?.website_title as any;
            const website_description = this.$currentUser()?.company
              ?.website_description as any;
            this.seoService.setTitle(website_title);
            this.seoService.setOneSeoTag('title', website_title);
            this.seoService.setOneSeoTag('description', website_description);
          }
        }

        if (event.urlAfterRedirects.indexOf('payment') !== -1) {
          this.isOnPayment = true;
        }
      });
    if (!this.$languageList().en.length) {
      this.languagesListStore.getListLanguage([] as any);
    }
  }

  isDefaultTheme = computed(() => {
    const currentUser = this.$currentUser();
    return (
      currentUser?.company?.web_theme &&
      currentUser?.company?.web_theme === 'default'
    );
  });

  isNewTheme = computed(() => {
    const currentUser = this.$currentUser();
    return (
      currentUser?.company?.web_theme &&
      currentUser?.company?.web_theme === 'wt2024_new'
    );
  });

  closeSplash() {
    this.localStorageJwtService.setItemSTR('off_splash', 1);
    this.splash_status = 0;
  }
  viewSlashUrl() {
    if (this.$currentUser()?._id) {
      // this.closeSplash();
      window.open(this.splash_url);
    }
  }

  toggleAiAssistant() {
    this.$isAiAssistantOpen.update((isOpen) => !isOpen);
    if (this.$isAiAssistantOpen()) {
      const userId = this.getAiAssistantHistoryUserId();
      if (userId && !this.$isAiAssistantLoading()) {
        this.loadAiAssistantHistory(userId, true);
      }
      this.ensureAiAssistantWelcomeMessage();
      this.scrollAiAssistantToBottom();
    }
  }

  toggleAiAssistantExpanded() {
    this.$isAiAssistantExpanded.update((isExpanded) => !isExpanded);
  }

  startNewAiAssistantChat() {
    if (this.$isAiAssistantLoading()) return;
    this.$aiAssistantMessages.set([this.createAiAssistantWelcomeMessage()]);
    this.$showHumanAgent.set(false);
    this.$showContactConfirmation.set(false);
    this.$showReportProblemOptions.set(false);
    this.aiAssistantInput = '';
    const userId = this.getAiAssistantHistoryUserId();
    if (userId)
      this.localStorageJwtService.removeItemSTR(
        this.getAiAssistantHistoryKey(userId),
      );
  }

  async sendAiAssistantMessage(
    contentOverride?: string,
    requestContentOverride?: string,
  ) {
    const content = (contentOverride ?? this.aiAssistantInput).trim();
    if (!content || this.$isAiAssistantLoading()) return;
    const requestContent = (requestContentOverride ?? content).trim();

    const requestMessages: AiAssistantMessage[] = [
      ...this.$aiAssistantMessages(),
      { role: 'user', content: requestContent },
    ];
    this.$aiAssistantMessages.set([
      ...this.$aiAssistantMessages(),
      { role: 'user', content },
      { role: 'assistant', content: '' },
    ]);
    this.saveAiAssistantHistory();
    this.scrollAiAssistantToBottom();
    this.$isAiAssistantLoading.set(true);
    this.$showHumanAgent.set(false);
    this.$showContactConfirmation.set(false);
    this.$showReportProblemOptions.set(false);
    this.aiAssistantInput = '';

    let token: string | null = null;
    this.localStorageJwtService.getItem().subscribe((value) => (token = value));

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60_000);

    try {
      const response = await fetch(`${environment.rest_api_url}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: requestMessages,
          assistantType: 'product',
        }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`AI Assistant request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const data = line.trim();
          if (!data.startsWith('data:')) continue;

          const raw = data.slice(5).trim();
          if (!raw || raw === '[DONE]') continue;

          const event = JSON.parse(raw) as {
            error?: string;
            choices?: Array<{ delta?: { content?: string } }>;
          };
          if (event.error) throw new Error(event.error);

          const responseContent = event.choices?.[0]?.delta?.content;
          if (responseContent) {
            this.appendAiAssistantContent(responseContent);
          }
        }
      }

      const assistantMessage = this.$aiAssistantMessages().at(-1);
      if (!assistantMessage?.content.trim()) {
        this.replaceLastAiAssistantMessage('No products found.');
      } else if (assistantMessage.content.includes('HUMAN_AGENT_REQUIRED')) {
        this.replaceLastAiAssistantMessage(
          'AI Assistant cannot answer this question.',
        );
        this.$showContactConfirmation.set(true);
      }
    } catch {
      this.replaceLastAiAssistantMessage(
        'AI Assistant is currently unable to respond. Please contact a support agent.',
      );
      this.$showContactConfirmation.set(true);
    } finally {
      clearTimeout(timeout);
      this.$isAiAssistantLoading.set(false);
      this.saveAiAssistantHistory();
    }
  }

  onAiAssistantKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      void this.sendAiAssistantMessage();
    }
  }

  sendAiAssistantContactRequest() {
    if (this.$isContactRequestSending()) return;

    const user = this.$currentUser();
    const fullName = String(user?.user_name || user?.full_name || '').trim();
    const email = String(user?.user_email || '').trim();

    if (!this.$loggedIn() || user?.default_user || !fullName || !email) {
      void this.openAiAssistantContactForm();
      return;
    }

    this.$isContactRequestSending.set(true);
    this.authStore
      .sendEmailContactUs({
        full_name: fullName,
        user_email: email,
        question: this.getAiAssistantEmailTranscript(),
        user_phone: user?.cellphone || '',
        toCustomer: 0,
      })
      .subscribe({
        next: (result) => {
          if (!result) return;
          this.$showContactConfirmation.set(false);
          this.addAiAssistantMessage(
            'Your contact request has been sent successfully.',
          );
        },
        error: () => {
          this.$isContactRequestSending.set(false);
          this.$showContactConfirmation.set(false);
          this.$showHumanAgent.set(true);
          this.addAiAssistantMessage(
            'Unable to send your contact request. Please use the contact form.',
          );
        },
        complete: () => this.$isContactRequestSending.set(false),
      });
  }

  declineAiAssistantContactRequest() {
    this.$showContactConfirmation.set(false);
    this.$showHumanAgent.set(true);
  }

  async openAiAssistantContactForm() {
    const navigated = await this.router.navigate(['/resources/contact-us'], {
      state: { aiAssistantChatHistory: this.getAiAssistantTranscript() },
    });
    if (navigated) this.$isAiAssistantOpen.set(false);
  }

  openAiAssistantLogin() {
    this.$isAiAssistantOpen.set(false);
    this.topbarService.callSignIn();
  }

  openAiAssistantForgotPassword() {
    this.$isAiAssistantOpen.set(false);
    this.topbarService.callForgotPassword();
  }

  startAiAssistantShoppingAdvice() {
    void this.sendAiAssistantMessage(
      'Shopping advice',
      'SHOPPING_ADVICE_START',
    );
  }

  openAiAssistantReportProblem() {
    if (this.$isAiAssistantLoading()) return;

    this.$aiAssistantMessages.update((messages) => [
      ...messages,
      { role: 'user', content: 'Report a problem' },
      {
        role: 'assistant',
        content: 'Describe your issue or choose one of the options below:',
      },
    ]);
    this.$showReportProblemOptions.set(true);
    this.$showHumanAgent.set(false);
    this.$showContactConfirmation.set(false);
    this.saveAiAssistantHistory();
    this.scrollAiAssistantToBottom();
  }

  selectAiAssistantReportProblem(problem: string, issueCode: string) {
    this.$showReportProblemOptions.set(false);
    void this.sendAiAssistantMessage(
      problem,
      [
        'REPORT_PROBLEM',
        `Issue code: ${issueCode}`,
        `Issue: ${problem}`,
        'Ask the customer for their order number or PO number if it has not been provided, then help resolve this issue.',
      ].join('\n'),
    );
  }

  getAiAssistantMessageSegments(content: string): AiAssistantMessageSegment[] {
    const segments: AiAssistantMessageSegment[] = [];
    const segmentPattern =
      /(\[\[PRODUCT_CARD\]\][\s\S]*?\[\[\/PRODUCT_CARD\]\])|(\[\[PRODUCT_DETAIL\]\][\s\S]*?\[\[\/PRODUCT_DETAIL\]\])|\[([^\]]+)]\((\/product\/[^)]+)\)|\[([^\]]+)]\((https?:\/\/[^)\s]+)\)/g;
    let lastIndex = 0;
    let productCount = 0;

    for (const match of content.matchAll(segmentPattern)) {
      if (productCount >= 10 || match.index === undefined) break;

      const text = content.slice(lastIndex, match.index);
      if (text && !(match[1] && !text.trim())) {
        segments.push({ type: 'text', text, url: '', productName: '' });
      }

      const cardBlock = match[1];
      const detailBlock = match[2];
      if (cardBlock) {
        const productName =
          cardBlock
            .match(/(?:^|\r?\n)NAME:[ \t]*(.+?)(?:\r?\n|$)/)?.[1]
            ?.trim() ?? '';
        const price =
          cardBlock
            .match(/(?:^|\r?\n)PRICE:[ \t]*(.+?)(?:\r?\n|$)/)?.[1]
            ?.trim() ?? '';
        const imageUrl =
          cardBlock
            .match(/(?:^|\r?\n)IMAGE:[ \t]*([^\r\n]*)(?:\r?\n|$)/)?.[1]
            ?.trim() ?? '';
        const reason =
          cardBlock
            .match(/(?:^|\r?\n)REASON:[ \t]*([^\r\n]*)(?:\r?\n|$)/)?.[1]
            ?.trim() ?? '';
        const review =
          cardBlock
            .match(/(?:^|\r?\n)REVIEW:[ \t]*([^\r\n]*)(?:\r?\n|$)/)?.[1]
            ?.trim() ?? '';
        const url =
          cardBlock
            .match(
              /(?:^|\r?\n)LINK:[ \t]*(\/product\/[^\r\n]+)(?:\r?\n|$)/,
            )?.[1]
            ?.trim() ?? '';

        if (productName && url) {
          segments.push({
            type: 'card',
            text: '',
            url,
            productName,
            price,
            imageUrl,
            reason,
            review,
          });
        } else {
          segments.push({
            type: 'text',
            text: cardBlock,
            url: '',
            productName: '',
          });
        }
      } else if (detailBlock) {
        const productName =
          detailBlock
            .match(/(?:^|\r?\n)NAME:[ \t]*(.+?)(?:\r?\n|$)/)?.[1]
            ?.trim() ?? '';
        const price =
          detailBlock
            .match(/(?:^|\r?\n)PRICE:[ \t]*(.+?)(?:\r?\n|$)/)?.[1]
            ?.trim() ?? '';
        const imageUrl =
          detailBlock
            .match(/(?:^|\r?\n)IMAGE:[ \t]*([^\r\n]*)(?:\r?\n|$)/)?.[1]
            ?.trim() ?? '';
        const optionNames = (
          detailBlock.match(
            /(?:^|\r?\n)OPTIONS:[ \t]*([^\r\n]*)(?:\r?\n|$)/,
          )?.[1] ?? ''
        )
          .split('|')
          .map((optionName) => optionName.trim())
          .filter(Boolean);
        const url =
          detailBlock
            .match(
              /(?:^|\r?\n)LINK:[ \t]*(\/product\/[^\r\n]+)(?:\r?\n|$)/,
            )?.[1]
            ?.trim() ?? '';

        if (productName && url) {
          segments.push({
            type: 'detail',
            text: '',
            url,
            productName,
            price,
            imageUrl,
            optionNames,
          });
        } else {
          segments.push({
            type: 'text',
            text: detailBlock,
            url: '',
            productName: '',
          });
        }
      } else if (match[4]) {
        const url = match[4];
        const productNameMatches = Array.from(
          text.matchAll(/\*\*([^*]+)\*\*/g),
        );
        const productName =
          productNameMatches.at(-1)?.[1]?.trim() ||
          url.replace('/product/', '');

        segments.push({
          type: 'product',
          text: 'View product',
          url,
          productName,
        });
      } else {
        segments.push({
          type: 'external',
          text: match[5],
          url: match[6],
          productName: '',
        });
      }
      lastIndex = match.index + match[0].length;
      productCount += 1;
    }

    const remainingText = content.slice(lastIndex);
    if (remainingText) {
      segments.push({
        type: 'text',
        text: remainingText,
        url: '',
        productName: '',
      });
    }

    const messageSegments: AiAssistantMessageSegment[] = segments.length
      ? segments
      : [{ type: 'text', text: content, url: '', productName: '' }];

    return messageSegments.flatMap((segment) =>
      segment.type === 'text'
        ? this.getAiAssistantBoldSegments(segment.text)
        : [segment],
    );
  }

  private getAiAssistantBoldSegments(
    content: string,
  ): AiAssistantMessageSegment[] {
    const segments: AiAssistantMessageSegment[] = [];
    const boldPattern =
      /\*\*([\s\S]+?)\*\*|\b(order number|PO number|email address)\b/gi;
    let lastIndex = 0;

    for (const match of content.matchAll(boldPattern)) {
      if (match.index === undefined) continue;

      const text = content.slice(lastIndex, match.index);
      if (text) {
        segments.push({ type: 'text', text, url: '', productName: '' });
      }

      segments.push({
        type: 'bold',
        text: match[1] ?? match[2],
        url: '',
        productName: '',
      });
      lastIndex = match.index + match[0].length;
    }

    const remainingText = content.slice(lastIndex);
    if (remainingText) {
      segments.push({
        type: 'text',
        text: remainingText,
        url: '',
        productName: '',
      });
    }

    return segments.length
      ? segments
      : [{ type: 'text', text: content, url: '', productName: '' }];
  }

  showAiAssistantProductDetail(productName: string, productUrl: string) {
    if (this.$isAiAssistantLoading()) return;
    const alias = productUrl.replace('/product/', '');
    void this.sendAiAssistantMessage(
      `Show option details for ${productName}.`,
      `Show option details for ${productName} using product alias "${alias}".`,
    );
  }

  formatAiAssistantPrice(price?: string) {
    if (!price) return '';
    const value = Number(price.replace(/[^0-9.-]/g, ''));
    if (!Number.isFinite(value)) return price;

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  getAiAssistantProductImage(imageUrl?: string) {
    if (!imageUrl) return '';
    if (/^https?:\/\//i.test(imageUrl)) return imageUrl;

    return `${environment.s3_url}/${imageUrl.replace(/^\/+/, '')}`;
  }

  private appendAiAssistantContent(content: string) {
    this.$aiAssistantMessages.update((messages) => {
      const updatedMessages = [...messages];
      const lastMessage = updatedMessages.at(-1);
      if (lastMessage?.role === 'assistant') {
        updatedMessages[updatedMessages.length - 1] = {
          ...lastMessage,
          content: lastMessage.content + content,
        };
      }
      return updatedMessages;
    });
    this.scrollAiAssistantToBottom();
  }

  private replaceLastAiAssistantMessage(content: string) {
    this.$aiAssistantMessages.update((messages) => {
      const updatedMessages = [...messages];
      const lastMessage = updatedMessages.at(-1);
      if (lastMessage?.role === 'assistant') {
        updatedMessages[updatedMessages.length - 1] = {
          ...lastMessage,
          content,
        };
      }
      return updatedMessages;
    });
    this.saveAiAssistantHistory();
    this.scrollAiAssistantToBottom();
  }

  private addAiAssistantMessage(content: string) {
    this.$aiAssistantMessages.update((messages) => [
      ...messages,
      { role: 'assistant', content },
    ]);
    this.saveAiAssistantHistory();
    this.scrollAiAssistantToBottom();
  }

  private getAiAssistantTranscript() {
    return this.$aiAssistantMessages()
      .filter((message) => message.content.trim())
      .map(
        (message) =>
          `${message.role === 'user' ? 'User' : 'AI Assistant'}: ${message.content.trim()}`,
      )
      .join('\n\n');
  }

  private getAiAssistantEmailTranscript() {
    return this.$aiAssistantMessages()
      .filter((message) => message.content.trim())
      .map((message) => {
        const role = message.role === 'user' ? 'User' : 'AI Assistant';
        const content = this.escapeAiAssistantEmailContent(
          message.content.trim(),
        ).replace(/\r?\n/g, '<br>');

        return `<div style="margin-bottom: 12px;"><strong>${role}:</strong> ${content}</div>`;
      })
      .join('');
  }

  private escapeAiAssistantEmailContent(content: string) {
    return content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/{/g, '&#123;')
      .replace(/}/g, '&#125;');
  }

  private getAiAssistantHistoryUserId() {
    const user = this.$currentUser();
    return String(user?._id || user?.id || user?.user_id || '');
  }

  private createAiAssistantWelcomeMessage(): AiAssistantMessage {
    return {
      role: 'assistant',
      content:
        "Hi! I'm your Online Assistant. Ask me anything about orders, designs, or shopping advice.\n\nIf I can't resolve your issue, I'll forward your message to one of our specialists.",
    };
  }

  private ensureAiAssistantWelcomeMessage() {
    if (this.$aiAssistantMessages().length === 0) {
      this.$aiAssistantMessages.set([this.createAiAssistantWelcomeMessage()]);
    }
  }

  private getAiAssistantHistoryKey(userId: string) {
    return `gotoprint-ai-assistant-history-${userId}`;
  }

  private loadAiAssistantHistory(userId: string, force = false) {
    if (!force && this.aiAssistantHistoryUserId === userId) return;
    this.aiAssistantHistoryUserId = userId;

    try {
      const history = JSON.parse(
        this.localStorageJwtService.getItemSTR(
          this.getAiAssistantHistoryKey(userId),
        ) ?? '[]',
      ) as AiAssistantMessage[];
      const validHistory = Array.isArray(history)
        ? history.filter(
            (message) =>
              (message.role === 'user' || message.role === 'assistant') &&
              typeof message.content === 'string' &&
              !!message.content.trim(),
          )
        : [];
      this.$aiAssistantMessages.set(validHistory);
    } catch {
      this.$aiAssistantMessages.set([]);
    }
  }

  private saveAiAssistantHistory() {
    const userId = this.getAiAssistantHistoryUserId();
    if (!userId) return;
    this.aiAssistantHistoryUserId = userId;
    const history = this.$aiAssistantMessages().filter((message) =>
      message.content.trim(),
    );
    try {
      this.localStorageJwtService.setItemSTR(
        this.getAiAssistantHistoryKey(userId),
        JSON.stringify(history),
      );
    } catch {
      return;
    }
  }

  private scrollAiAssistantToBottom() {
    setTimeout(() => {
      const content = this.aiAssistantContent?.nativeElement;
      if (content) content.scrollTop = content.scrollHeight;
    });
  }
}
